/**
 * Multicall3 Service
 *
 * Batches multiple contract read calls into a single RPC request,
 * reducing network overhead and ensuring data consistency (same block).
 *
 * Features:
 * - Automatic retry with exponential backoff for transient failures
 * - allowFailure flag for graceful handling of individual call failures
 *
 * Usage:
 *   const multicall = new MulticallService(provider, multicallAddress)
 *   const results = await multicall.call([
 *     multicall.encodeCall(contractAddress, contractInterface, 'functionName', [args]),
 *     multicall.encodeCall(anotherAddress, anotherInterface, 'anotherFunction', []),
 *   ])
 *   const decodedResult = multicall.decodeResult(results[0], contractInterface, 'functionName')
 */

import { Contract, Interface, JsonRpcProvider, InterfaceAbi } from 'ethers'
import { Multicall3ABI } from '@/abi/Multicall3ABI'
import { reportRpcError, reportRpcSuccess } from '@/lib/chainProvider'

// Retry configuration
const MAX_RETRIES = 3
const INITIAL_DELAY_MS = 1000 // 1 second
const BACKOFF_MULTIPLIER = 2 // 1s -> 2s -> 4s

/**
 * Check if an error is retryable (network/RPC issues, not contract reverts)
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    // Retry on network/timeout/rate limit errors
    if (
      message.includes('timeout') ||
      message.includes('network') ||
      message.includes('econnrefused') ||
      message.includes('econnreset') ||
      message.includes('socket') ||
      message.includes('rate limit') ||
      message.includes('too many requests') ||
      message.includes('429') ||
      message.includes('503') ||
      message.includes('502') ||
      message.includes('server error')
    ) {
      return true
    }
  }
  return false
}

/**
 * Check if an error is specifically a rate limit (429) error
 */
function isRateLimitError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return (
      message.includes('429') ||
      message.includes('rate limit') ||
      message.includes('too many requests')
    )
  }
  return false
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export interface Call3 {
  target: string
  allowFailure: boolean
  callData: string
}

export interface CallResult {
  success: boolean
  returnData: string
}

export interface EncodedCall {
  target: string
  allowFailure: boolean
  callData: string
  interface: Interface
  functionName: string
}

export class MulticallService {
  private contract: Contract
  private chainId: number | undefined

  constructor(provider: JsonRpcProvider, multicallAddress: string, chainId?: number) {
    this.contract = new Contract(multicallAddress, Multicall3ABI, provider)
    this.chainId = chainId
  }

  /**
   * Encode a contract call for batching
   */
  encodeCall(
    target: string,
    abi: InterfaceAbi | Interface,
    functionName: string,
    args: unknown[] = [],
    allowFailure = false
  ): EncodedCall {
    const iface = abi instanceof Interface ? abi : new Interface(abi as InterfaceAbi)
    const callData = iface.encodeFunctionData(functionName, args)

    return {
      target,
      allowFailure,
      callData,
      interface: iface,
      functionName,
    }
  }

  /**
   * Execute batched calls via Multicall3.aggregate3
   * Uses staticCall since aggregate3 is marked payable but we only need read operations
   * Includes retry with exponential backoff for transient failures
   * Returns raw results array
   */
  async call(calls: EncodedCall[]): Promise<CallResult[]> {
    const callStructs: Call3[] = calls.map((c) => ({
      target: c.target,
      allowFailure: c.allowFailure,
      callData: c.callData,
    }))

    let lastError: unknown
    let delay = INITIAL_DELAY_MS

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        // Use staticCall to force read-only execution (aggregate3 is payable but we're just reading)
        const results: CallResult[] = await this.contract.aggregate3.staticCall(callStructs)

        // Report success to reset rate limit counter
        if (this.chainId !== undefined) {
          reportRpcSuccess(this.chainId)
        }

        return results
      } catch (error) {
        lastError = error

        // Don't retry on non-retryable errors (contract reverts, etc.)
        if (!isRetryableError(error)) {
          throw error
        }

        // Report rate limit error to trigger fallback switching when threshold is exceeded
        if (this.chainId !== undefined && isRateLimitError(error)) {
          reportRpcError(this.chainId)
        }

        // Don't retry if we've exhausted attempts
        if (attempt >= MAX_RETRIES) {
          console.warn(`[Multicall3] Failed after ${MAX_RETRIES + 1} attempts`)
          throw error
        }

        // Log retry attempt and wait
        console.warn(`[Multicall3] Attempt ${attempt + 1} failed, retrying in ${delay}ms...`)
        await sleep(delay)
        delay *= BACKOFF_MULTIPLIER
      }
    }

    // Should never reach here, but TypeScript needs it
    throw lastError
  }

  /**
   * Decode a single result using the interface and function name
   */
  decodeResult<T>(
    result: CallResult,
    iface: Interface,
    functionName: string
  ): T | null {
    if (!result.success) {
      return null
    }
    // Empty returnData means the target wasn't a contract (EVM returns success with no data for EOAs)
    if (!result.returnData || result.returnData === '0x') {
      return null
    }
    const decoded = iface.decodeFunctionResult(functionName, result.returnData)
    // If single return value, return it directly; otherwise return the full result
    return decoded.length === 1 ? (decoded[0] as T) : (decoded as T)
  }

  /**
   * Execute calls and decode all results in one step
   * Returns an array of decoded values (null for failed calls)
   */
  async callAndDecode<T extends unknown[]>(calls: EncodedCall[]): Promise<T> {
    const results = await this.call(calls)

    return results.map((result, i) => {
      const call = calls[i]
      return this.decodeResult(result, call.interface, call.functionName)
    }) as T
  }
}

/**
 * Create a MulticallService instance
 * Pass chainId to enable automatic RPC fallback switching on rate limit errors.
 */
export function createMulticallService(
  provider: JsonRpcProvider,
  multicallAddress: string,
  chainId?: number
): MulticallService {
  return new MulticallService(provider, multicallAddress, chainId)
}
